import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Sequences, series and power series — the second half of Calculus II.
 *
 * This is the part of the course with no Grade 12 counterpart, and the part
 * first-year students fail. The reason is not that the tests are hard; each one
 * is a short calculation. It is that the whole unit asks a *yes-or-no* question
 * where every previous chapter of calculus asked for a number, and a student who
 * has spent five years being graded on arithmetic has no habit for producing an
 * argument instead.
 *
 * Three decisions shape the unit.
 *
 * **A sequence and its series get separate lessons.** They are separate objects
 * with separate limits, and running them together is what produces the belief
 * that a series converges whenever its terms shrink — the single most common
 * false statement in first-year mathematics. Split apart, the test for
 * divergence can be stated honestly as a one-way implication, and the harmonic
 * series stops being an unexplained exception and becomes the point.
 *
 * **The tests are ordered by what each one can decide, not by tradition.**
 * `integral-test` comes first because it *proves* the p-series result, and
 * `comparison-tests` is useless without a stock of known series to compare
 * against. `ratio-test` is last of the positive-term tests because it is the
 * only one that looks at nothing but the ratio — and because it is the test
 * power series need.
 *
 * **Every test lesson states its own failure mode.** A test that returns "no
 * information" is not a broken test, and the ratio test returning L = 1 on every
 * p-series is the clearest example of a correct calculation that decides
 * nothing. A student who has never been told this reads L = 1 as convergence,
 * because every other calculation in mathematics they have ever done produced an
 * answer.
 *
 * `power-series` carries the radius of convergence rather than leaving it its
 * own concept, because they are one calculation: the ratio test applied to a
 * series with an x in it returns the radius as its answer. Splitting them would
 * mean teaching the same limit twice and calling it two topics.
 *
 * `taylor-series` closes the unit on the one idea that makes all of it worth
 * doing — that sin, cos, e^x and ln are, in the end, polynomials with no last
 * term, and that this is how a calculator has ever computed any of them.
 */
export const SERIES_LESSONS: Lesson[] = [
  lesson({
    id: 'sequence-limits-01',
    unitId: 'unit-infinite-series',
    conceptId: 'sequence-limits',
    title: 'Sequences and Their Limits',
    summary: 'An infinite list of numbers, and whether it settles anywhere — the limit taken along the integers.',
    objectives: [
      'Decide whether a sequence converges, and find its limit when it does',
      'Use the growth hierarchy to compare factorials, exponentials, powers and logarithms',
      'Distinguish a sequence from the series built out of it',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A limit along the integers',
        blocks: [
          text('A **sequence** is an infinite list of numbers with a first one, a second one, and so on forever. It is written aₙ, and n runs through 1, 2, 3, …'),
          math('a_{n} = \\frac{1}{n}: \\quad 1,\\ \\tfrac{1}{2},\\ \\tfrac{1}{3},\\ \\tfrac{1}{4},\\ \\ldots'),
          text('The only question this lesson asks is whether the list settles down. If the terms get and stay arbitrarily close to some number L, the sequence **converges** to L; otherwise it **diverges**.'),
          math('\\lim_{n \\to \\infty} a_{n} = L'),
          insight('This is the same limit at infinity you met for functions, restricted to whole-number inputs. That restriction costs nothing when the formula makes sense for real x — every technique for limits at infinity carries straight over — and it matters enormously for things like n!, which has no meaning at x = 2.5 and so has no function to borrow from.'),
        ],
      },
      intuition: {
        title: 'Four tools and one hierarchy',
        blocks: [
          text('**Divide by the highest power.** For a ratio of polynomials in n, this is the whole method, exactly as it was for rational functions:'),
          math('\\lim_{n\\to\\infty}\\frac{3n^{2} + 2}{5n^{2} - n} = \\lim_{n\\to\\infty}\\frac{3 + 2/n^{2}}{5 - 1/n} = \\frac{3}{5}'),
          text('**Geometric terms.** The behaviour of rⁿ depends entirely on the size of r, and this one is worth knowing without thinking:'),
          table(['Value of r', 'What rⁿ does'], [
            ['|r| < 1', 'converges to 0'],
            ['r = 1', 'constant at 1'],
            ['r = −1', 'alternates between 1 and −1: diverges'],
            ['|r| > 1', 'grows without bound: diverges'],
          ]),
          text('**Borrow from functions.** If aₙ = f(n) for a function f you can differentiate, the limit of f at infinity is the limit of the sequence — which is what lets L\'Hôpital\'s rule be used on a sequence at all. The implication runs one way only: the sequence (−1)ⁿ has no function limit and no sequence limit, but sin(nπ) is constantly 0 as a sequence while sin(πx) does not settle.'),
          text('**The growth hierarchy.** Most limits in this course are settled by knowing which of two things grows faster. From slowest to fastest:'),
          math('\\ln n \\;\\ll\\; n^{p} \\;\\ll\\; b^{n} \\;\\ll\\; n! \\;\\ll\\; n^{n}'),
          insight('Each of these beats everything to its left decisively — the ratio of a later one to an earlier one tends to infinity, however big the exponent p or the base b. So 2ⁿ/n¹⁰⁰ → ∞ and n!/2ⁿ → ∞, even though the first thousand terms of each suggest otherwise. Sequences are one of the few places in mathematics where computing a few terms is actively misleading.'),
          warning('A sequence and the series made from it are different objects, and they are about to have different names that look almost identical. aₙ is the n-th *term*; Σaₙ is the *sum*. The sequence 1/n converges to 0. The series 1 + ½ + ⅓ + … does not converge to anything. Both statements are true, and the next lesson is about why.', 'The sequence is not the series'),
          text('**Monotone and bounded.** A sequence that is always increasing and never exceeds some ceiling must converge — it has nowhere else to go. This is the one convergence result that identifies no limit at all, and it is the standard way to prove a recursively defined sequence settles when there is no formula to take a limit of.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'dot-plot', values: [10, 5, 3, 3, 2, 2, 2, 2, 1, 1, 1, 1, 1, 1, 1], from: 0, to: 11, label: 'aₙ = 10/n, rounded' },
            'A convergent sequence, plotted as the values it takes. The terms crowd towards 0 and stay there: after some point every term is within any gap you care to name. Convergence is a statement about the *tail* — the first thousand terms can do anything at all without affecting it.',
          ),
          insight('That last sentence is the practical consequence worth carrying into the rest of the unit. Changing, deleting or inserting finitely many terms cannot change whether a sequence converges, and cannot change whether a series converges either — though for a series it certainly changes the sum.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Does the sequence aₙ = (2n² − n)/(3n² + 5) converge? If so, to what?'),
          steps([
            { label: 'Top and bottom both grow like n², so divide through by n².', latex: 'a_{n} = \\frac{2 - 1/n}{3 + 5/n^{2}}' },
            { label: 'Every term with an n underneath tends to 0.', latex: '\\to \\frac{2 - 0}{3 + 0}' },
            { label: 'The limit.', latex: '\\lim_{n\\to\\infty} a_{n} = \\frac{2}{3}' },
          ]),
          text('And one from the hierarchy: does aₙ = n²/2ⁿ converge?'),
          steps([
            { label: 'Compare the growth rates rather than computing.', latex: 'n^{2} \\ll 2^{n}' },
            { label: 'An exponential beats every power, so the denominator wins.', latex: '\\lim_{n\\to\\infty}\\frac{n^{2}}{2^{n}} = 0' },
          ]),
          tip('The second one is worth checking numerically once, because it does not look true early on: a₁ = 0.5, a₄ = 1, a₁₀ ≈ 0.098, a₂₀ ≈ 0.00038. The sequence rises before it falls. Any conclusion drawn from the first few terms would have been the wrong one.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('sequence-limits', 4)] },
      independent: { title: 'On your own', blocks: [practice('sequence-limits', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('sequence-limits', 3, 'Ratios of polynomials, geometric terms, and limits decided by the growth hierarchy.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A sequence converges when its terms settle at a single number.',
            'Ratios of polynomials in n: divide by the highest power.',
            'rⁿ → 0 exactly when |r| < 1; it diverges for |r| > 1 and for r = −1.',
            'Growth hierarchy: ln n ≪ n^p ≪ bⁿ ≪ n! ≪ nⁿ.',
            'Monotone and bounded means convergent, even when the limit is unknown.',
            'The sequence aₙ and the series Σaₙ are different objects with different answers.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'series-convergence-01',
    unitId: 'unit-infinite-series',
    conceptId: 'series-convergence',
    title: 'Series and the Test for Divergence',
    summary: 'Adding a sequence up forever, what that can possibly mean, and the one thing shrinking terms do not guarantee.',
    objectives: [
      'Define the sum of a series as the limit of its partial sums',
      'Evaluate a geometric series and a telescoping series exactly',
      'Apply the test for divergence, and say why it can never prove convergence',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Adding up a list with no end',
        blocks: [
          text('Adding two numbers is defined. Adding a hundred is defined. Adding infinitely many is not — there is no last step, so there is nothing for the answer to be, until somebody says what it should mean.'),
          text('The definition everyone agreed on is a limit. Add the first n terms; call that the n-th **partial sum**; then see whether *those* settle:'),
          math('s_{n} = a_{1} + a_{2} + \\cdots + a_{n}, \\qquad \\sum_{n=1}^{\\infty} a_{n} = \\lim_{n\\to\\infty} s_{n}'),
          insight('So a series is not a new kind of object at all. It is a *sequence* — the sequence of partial sums — and the previous lesson already says what its convergence means. Everything in this unit follows from that one sentence.'),
          text('If the partial sums converge, the series **converges** and its sum is that limit. If not, the series **diverges** and has no sum.'),
        ],
      },
      intuition: {
        title: 'Two series you can add up exactly, and one you cannot',
        blocks: [
          text('**Geometric.** Each term is a fixed multiple of the one before. Precalculus already found the partial sum; the limit of it is what makes the infinite version work:'),
          math('\\sum_{n=0}^{\\infty} a r^{n} = \\frac{a}{1 - r} \\qquad \\text{provided } |r| < 1'),
          warning('The condition is not a technicality. For |r| ≥ 1 the terms do not shrink, the partial sums run away, and the formula a/(1 − r) still returns a number — a wrong one. Applying it to r = 2 gives −1 for a sum of positive terms, which is the clearest possible sign that the condition was doing real work.', 'Check |r| < 1 before using the formula'),
          text('**Telescoping.** When each term splits into a difference, consecutive pieces cancel and the partial sum collapses to its two ends:'),
          math('\\frac{1}{n(n+1)} = \\frac{1}{n} - \\frac{1}{n+1}'),
          math('s_{n} = \\left(1 - \\tfrac{1}{2}\\right) + \\left(\\tfrac{1}{2} - \\tfrac{1}{3}\\right) + \\cdots + \\left(\\tfrac{1}{n} - \\tfrac{1}{n+1}\\right) = 1 - \\frac{1}{n+1}'),
          text('so the sum is exactly 1. Telescoping and geometric are, between them, essentially the only series in this course whose *sum* you can find. Everything after this lesson decides whether a sum exists without ever producing it.'),
          insight('That is worth saying plainly, because it is the shift that catches students out. "Does this converge?" is a different question from "what does it equal", and from here on the course asks only the first one. A test that answers "yes, it converges" and offers no number is not an incomplete answer — it is the answer.'),
          text('**The test for divergence.** If a series converges, its terms must go to zero. Contrapositive:'),
          math('\\text{if } \\lim_{n\\to\\infty} a_{n} \\neq 0, \\text{ then } \\sum a_{n} \\text{ diverges}'),
          warning('The converse is false, and it is the most important false statement in this course. Terms tending to 0 does **not** imply convergence. The witness is the harmonic series: 1/n tends to 0, and 1 + ½ + ⅓ + ¼ + … grows without bound. It grows slowly — the first hundred terms reach only about 5.2 — but it passes every bound eventually.', 'Shrinking terms are not enough'),
          text('Why the harmonic series diverges, in one line: group the terms as ½, then ⅓ + ¼, then ⅕ through ⅛, and so on. Each group is at least ½, and there are infinitely many groups.'),
          tip('Use the test for divergence first, always. It is the cheapest test there is — a single limit — and when it fires you are finished. When it does not, you have learned nothing and must reach for a real test, which is exactly what the next four lessons are.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'bar-chart', bars: [
              { label: 's₁', value: 50 },
              { label: 's₂', value: 75 },
              { label: 's₃', value: 88 },
              { label: 's₄', value: 94 },
              { label: 's₅', value: 97 },
              { label: 's₆', value: 98 },
            ], unitLabel: '% of the sum' },
            'The partial sums of ½ + ¼ + ⅛ + …, as a percentage of their limit. Each one closes half the remaining gap, so the sequence of partial sums climbs towards 1 and never reaches it. The *series* converges to 1; the *sequence* of terms converges to 0. Two different statements about the same list of numbers.',
          ),
          insight('Now picture the same chart for the harmonic series. The bars still rise more and more slowly — the terms still tend to zero — and they never stop rising. Slowing down is not the same as stopping, and no picture of finitely many partial sums can tell the two cases apart. That is precisely why tests are needed instead of graphs.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Decide each of these, and give the sum where there is one.'),
          steps([
            { label: 'Σ 3(2/5)ⁿ from n = 0. Geometric with r = 2/5, and |r| < 1.', latex: '\\frac{3}{1 - 2/5} = \\frac{3}{3/5} = 5' },
            { label: 'Σ (4n + 1)/(2n + 7). Check the terms first.', latex: '\\lim_{n\\to\\infty}\\frac{4n+1}{2n+7} = 2 \\neq 0' },
            { label: 'Terms do not tend to zero, so it diverges. No sum exists.', latex: '\\text{divergent, by the test for divergence}' },
            { label: 'Σ 1/(n(n+1)) from n = 1. Split into partial fractions.', latex: '\\frac{1}{n} - \\frac{1}{n+1}' },
            { label: 'The partial sum telescopes.', latex: 's_{n} = 1 - \\frac{1}{n+1} \\;\\longrightarrow\\; 1' },
          ]),
          insight('The middle one took a single limit and no cleverness — and it is the only one of the three where a wrong method would have gone unnoticed, because a divergent series will happily accept any formula you apply to it and hand back a number.'),
          warning('When the test for divergence gives 0, write down "no information" rather than a conclusion. The terms of 1/n, of 1/n², and of 1/√n all tend to 0, and those three series behave in three different ways — one convergent, two divergent, and one of the divergent ones only barely.', 'A limit of zero decides nothing'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('series-convergence', 4)] },
      independent: { title: 'On your own', blocks: [practice('series-convergence', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('series-convergence', 3, 'Geometric sums, telescoping sums, and the test for divergence applied where it decides nothing.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A series is the limit of its partial sums — a sequence in disguise.',
            'Geometric: Σarⁿ = a/(1 − r), and only when |r| < 1.',
            'Telescoping: split the term into a difference and watch the middle cancel.',
            'If aₙ does not tend to 0, the series diverges. Check this first, every time.',
            'If aₙ does tend to 0, you have learned nothing at all.',
            'The harmonic series is the standing counterexample: terms → 0, sum → ∞.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'integral-test-01',
    unitId: 'unit-infinite-series',
    conceptId: 'integral-test',
    title: 'The Integral Test and p-Series',
    summary: 'Comparing a sum with the area under a curve, and settling the family of series every later test measures against.',
    objectives: [
      'State the three conditions the integral test requires, and check them',
      'Decide a series by evaluating the matching improper integral',
      'Use the p-series result to classify Σ1/n^p at a glance',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'A sum is a row of rectangles',
        blocks: [
          text('The harmonic series diverges, and the grouping argument that proves it is clever, specific and useless for anything else. Here is an argument that generalises.'),
          text('Draw the curve y = 1/x, and beside it draw rectangles of width 1 and heights 1, ½, ⅓, … The rectangles are the terms of the series; the curve is a function you can integrate. Line them up and each becomes a bound on the other.'),
          insight('That is the whole of the integral test. A series of positive decreasing terms and the integral of the matching function are trapped within one term of each other, so either both are finite or both are infinite. It converts a question about sums — where you have almost no tools — into a question about integrals, where the whole of the last unit applies.'),
        ],
      },
      intuition: {
        title: 'The test, and the family it settles',
        blocks: [
          text('**The integral test.** Suppose f is **positive**, **continuous** and **decreasing** on [1, ∞), and aₙ = f(n). Then'),
          math('\\sum_{n=1}^{\\infty} a_{n} \\text{ converges} \\iff \\int_{1}^{\\infty} f(x)\\,dx \\text{ converges}'),
          warning('All three conditions matter. Positive and decreasing are what make the rectangles bound the curve at all; if the terms wander up and down, the picture is not a bound and the test says nothing. Check them before applying it — and note that "eventually decreasing" is enough, because finitely many terms never change convergence.', 'Positive, continuous, decreasing'),
          text('**What the test does not give you.** The integral and the sum are not equal. They are within one term of each other:'),
          math('\\int_{1}^{\\infty} f \\;\\leq\\; \\sum_{n=1}^{\\infty} a_{n} \\;\\leq\\; a_{1} + \\int_{1}^{\\infty} f'),
          insight('So ∫ from 1 to ∞ of dx/x² = 1 tells you Σ1/n² converges, and tells you the sum is between 1 and 2. It does not tell you the sum is 1. (It is π²/6 ≈ 1.6449, which took Euler to find and is not obtainable from this test at all.)'),
          text('**The p-series.** Apply the test to f(x) = x^(−p) and the improper-integral result from the last unit answers immediately:'),
          math('\\sum_{n=1}^{\\infty}\\frac{1}{n^{p}} \\quad\\text{converges} \\iff p > 1'),
          table(['Series', 'p', 'Verdict'], [
            ['Σ1/n', '1', 'diverges — the harmonic series'],
            ['Σ1/n²', '2', 'converges'],
            ['Σ1/√n', '½', 'diverges'],
            ['Σ1/n^{1.001}', '1.001', 'converges, extremely slowly'],
            ['Σ1/n^{0.999}', '0.999', 'diverges'],
          ]),
          insight('The last two rows are the point of the whole table. Nothing about the terms distinguishes them — both shrink, both shrink at almost exactly the same rate — and they sit on opposite sides of the answer. Convergence is not something you can see; p > 1 is a genuinely sharp boundary, and this is the family every other test in the course compares against.'),
          tip('Learn the p-series result rather than re-deriving it. Every comparison you make from the next lesson onwards will be against a p-series or a geometric series, and being able to classify one instantly is most of the work.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'reciprocal',
              shade: { from: 1, to: 6, rectangles: 5 },
              asymptotes: { vertical: [0], horizontal: [0] },
              min: 0,
              max: 7,
              label: 'y = 1/x',
            },
            'The picture behind the test. Each rectangle has width 1 and its height is a term of the series. Drawn with the left endpoint, as here, every rectangle sits above the curve — so the sum exceeds the integral, and if the integral is infinite the sum must be too. Drawn with the right endpoint instead, the rectangles sit below, and the inequality reverses to bound the sum from above.',
          ),
          insight('Two drawings, two inequalities, and between them they say the sum and the integral live or die together. It is worth sketching both once: the direction of each bound is the only part of this test anyone gets wrong.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Does Σ n/(n² + 1) converge, summed from n = 1?'),
          steps([
            { label: 'Terms first, from the cheapest test.', latex: '\\lim_{n\\to\\infty}\\frac{n}{n^{2}+1} = 0', note: 'No information — as expected. Now do some work.' },
            { label: 'Check the three conditions for f(x) = x/(x² + 1).', latex: 'f > 0 \\text{ on } [1,\\infty),\\ \\text{continuous},\\ \\text{decreasing for } x \\geq 1' },
            { label: 'Set up the improper integral.', latex: '\\int_{1}^{\\infty}\\frac{x}{x^{2}+1}\\,dx = \\lim_{t\\to\\infty}\\int_{1}^{t}\\frac{x\\,dx}{x^{2}+1}' },
            { label: 'Substitute u = x² + 1, du = 2x dx.', latex: '= \\lim_{t\\to\\infty}\\tfrac{1}{2}\\Big[\\ln(x^{2}+1)\\Big]_{1}^{t}' },
            { label: 'Evaluate the limit.', latex: '= \\lim_{t\\to\\infty}\\tfrac{1}{2}\\big(\\ln(t^{2}+1) - \\ln 2\\big) = \\infty' },
            { label: 'The integral diverges, so the series does too.', latex: '\\sum \\frac{n}{n^{2}+1} \\text{ diverges}' },
          ]),
          insight('Sanity-check that against instinct: n/(n² + 1) behaves like 1/n for large n, and Σ1/n diverges. The integral test confirmed what a glance at the leading behaviour suggested — and the next lesson turns that glance into a test of its own, so this integral never has to be done again.'),
          warning('Do not report ∞ as the sum, and do not report the value of a convergent integral as the sum of the series. The test compares them; it does not equate them. A convergent integral of 1 sits alongside a sum that could be anything between 1 and 1 + a₁.', 'The integral is not the sum'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('integral-test', 4)] },
      independent: { title: 'On your own', blocks: [practice('integral-test', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('integral-test', 3, 'p-series at a glance, the three conditions, and series that need the integral actually evaluated.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'f positive, continuous and decreasing, with aₙ = f(n): sum and integral converge together.',
            'The integral is not the sum — it is within one term of it.',
            'Σ1/n^p converges exactly when p > 1.',
            'p = 1 is the harmonic series and diverges; p = 0.999 diverges and p = 1.001 converges.',
            'Check the three conditions before applying the test, and "eventually" is enough.',
            'p-series and geometric series are the stock every later comparison is made against.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'comparison-tests-01',
    unitId: 'unit-infinite-series',
    conceptId: 'comparison-tests',
    title: 'Comparison and Limit Comparison',
    summary: 'Deciding a series by holding it beside one you already know — and the second version, for when the inequality points the wrong way.',
    objectives: [
      'Apply the direct comparison test, with the inequality in the direction that concludes',
      'Choose a comparison series from the leading behaviour of the terms',
      'Use the limit comparison test when the inequality is awkward or false',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'You already know enough series',
        blocks: [
          text('After the last lesson you can classify two whole families at sight: geometric series by their ratio, and p-series by their exponent. That is a large stock of known answers, and most series you meet are *almost* one of them.'),
          math('\\sum \\frac{1}{n^{2} + 3n + 7}'),
          text('That is not a p-series. But for large n the 3n and the 7 are irrelevant beside the n², and the terms behave like 1/n², which converges. This lesson makes that reasoning into something you are allowed to write down.'),
          insight('There are two versions, and the second exists because the first is fussy. Direct comparison needs an inequality that actually holds; limit comparison needs only that the two series *behave* alike, which is what you noticed in the first place.'),
        ],
      },
      intuition: {
        title: 'Two tests, and which way the inequality has to point',
        blocks: [
          text('**Direct comparison.** Suppose 0 ≤ aₙ ≤ bₙ for all n. Then:'),
          list([
            'If Σbₙ converges, so does Σaₙ. Something trapped under a finite total is finite.',
            'If Σaₙ diverges, so does Σbₙ. Something above an infinite total is infinite.',
          ]),
          warning('The other two combinations tell you nothing at all, and this is where the test is misused. A series *smaller* than a divergent one may converge or diverge — 1/n² is smaller than 1/n and converges, while 1/(2n) is smaller than 1/n and diverges. "Smaller than infinite" is not a constraint.', 'Small and divergent, large and convergent'),
          text('So the direction matters, and the useful pairing is: to prove convergence, bound **above** by something convergent; to prove divergence, bound **below** by something divergent.'),
          text('**Choosing the comparison.** Keep the leading behaviour and throw everything else away:'),
          table(['Series', 'Behaves like', 'Because'], [
            ['1/(n² + 3n + 7)', '1/n²', 'the n² dominates the denominator'],
            ['(2n + 1)/(n³ − 4)', '2/n²', 'degree 1 over degree 3'],
            ['1/√(n + 5)', '1/√n', 'the 5 is irrelevant far out'],
            ['(3ⁿ + 1)/4ⁿ', '(3/4)ⁿ', 'the 1 is negligible beside 3ⁿ'],
          ]),
          text('**Limit comparison.** If aₙ and bₙ are positive and'),
          math('\\lim_{n\\to\\infty}\\frac{a_{n}}{b_{n}} = L \\quad\\text{with}\\quad 0 < L < \\infty'),
          text('then Σaₙ and Σbₙ do the same thing. No inequality is required, and no direction has to be got right.'),
          insight('This is why limit comparison is the test people actually use. For 1/(n² − 3n) the inequality against 1/n² points the *wrong* way — the terms are bigger, not smaller — so direct comparison proves nothing, while the limit of the ratio is 1 and the test concludes in a single line. Whenever the leading behaviour is obvious and the inequality is not, take the limit.'),
          warning('L must be a finite non-zero number. L = 0 or L = ∞ means the two series do not behave alike, and the test is inconclusive as stated — it has one-directional refinements, but reaching for a better comparison series is faster than remembering them.', 'The limit has to be strictly between 0 and ∞'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'bar-chart', bars: [
              { label: 'n=1', value: 100 },
              { label: 'n=2', value: 25 },
              { label: 'n=3', value: 11 },
              { label: 'n=4', value: 6 },
              { label: 'n=5', value: 4 },
              { label: 'n=6', value: 3 },
            ], unitLabel: '1/n², ×100' },
            'The terms of Σ1/n², which converges. Any series whose terms are trapped beneath these bars — for every n, not just eventually visibly — converges as well. The comparison test is exactly this picture: one row of bars under another, and a finite total that the smaller one cannot escape.',
          ),
          insight('Notice that "trapped beneath" has to hold for the tail, not for a few terms you checked. A comparison that fails at n = 3 but holds ever after is still fine — finitely many terms never decide convergence — but a comparison that holds for the first six terms and fails afterwards proves nothing whatsoever.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Decide Σ 1/(n² + 3n + 7) by direct comparison.'),
          steps([
            { label: 'Guess the behaviour: the n² dominates.', latex: '\\frac{1}{n^{2} + 3n + 7} \\approx \\frac{1}{n^{2}}' },
            { label: 'Get the inequality pointing the right way for convergence.', latex: 'n^{2} + 3n + 7 > n^{2} \\quad\\Longrightarrow\\quad \\frac{1}{n^{2}+3n+7} < \\frac{1}{n^{2}}', note: 'A bigger denominator makes a smaller fraction — the step where a sign slip is fatal.' },
            { label: 'The larger series is a p-series with p = 2.', latex: '\\sum \\frac{1}{n^{2}} \\text{ converges}' },
            { label: 'Trapped below something finite.', latex: '\\sum\\frac{1}{n^{2}+3n+7} \\text{ converges}' },
          ]),
          text('Now one where direct comparison fails: Σ 1/(n² − 3n), summed from n = 4 so the denominator stays positive.'),
          steps([
            { label: 'The same guess, but now the denominator is smaller than n².', latex: '\\frac{1}{n^{2}-3n} > \\frac{1}{n^{2}}' },
            { label: 'Larger than a convergent series — no conclusion. Switch tests.', latex: '\\text{direct comparison: inconclusive}' },
            { label: 'Take the ratio against the same comparison series.', latex: '\\frac{a_{n}}{b_{n}} = \\frac{n^{2}}{n^{2}-3n} = \\frac{1}{1 - 3/n}' },
            { label: 'The limit is finite and non-zero.', latex: '\\lim_{n\\to\\infty}\\frac{a_{n}}{b_{n}} = 1' },
            { label: 'So the two series do the same thing, and Σ1/n² converges.', latex: '\\sum\\frac{1}{n^{2}-3n} \\text{ converges}' },
          ]),
          tip('When in doubt, go straight to limit comparison. It concludes in every case direct comparison does, it needs no inequality, and the only thing you must get right is the comparison series — which is read off the leading behaviour in a second.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('comparison-tests', 4)] },
      independent: { title: 'On your own', blocks: [practice('comparison-tests', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('comparison-tests', 3, 'Picking the comparison series, getting the inequality the right way round, and knowing when to switch to the limit form.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Direct comparison: smaller than convergent ⇒ convergent; bigger than divergent ⇒ divergent.',
            'The other two directions conclude nothing.',
            'Choose the comparison by leading behaviour: keep the dominant power, discard the rest.',
            'Limit comparison: if aₙ/bₙ → L with 0 < L < ∞, the two series agree.',
            'Limit comparison needs no inequality, which is why it is usually the faster route.',
            'Finitely many terms never matter; the comparison only has to hold eventually.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'ratio-test-01',
    unitId: 'unit-infinite-series',
    conceptId: 'ratio-test',
    title: 'The Ratio and Root Tests',
    summary: 'Asking only how each term compares with the one before it — the test that never looks at another series.',
    objectives: [
      'Compute the ratio limit L and read the verdict off it',
      'Recognise that L = 1 is genuinely no information, not a near miss',
      'Apply the root test to terms that are an n-th power',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Comparison, without a series to compare with',
        blocks: [
          text('Every test so far has needed something else: an integral to evaluate, or a known series to hold the terms against. For a series with a factorial in it, neither is available.'),
          math('\\sum_{n=1}^{\\infty}\\frac{2^{n}}{n!}'),
          text('n! has no continuous version to integrate, and comparing it against a p-series or a geometric series is awkward in both directions. What *is* easy is comparing each term with the one before it.'),
          math('\\frac{a_{n+1}}{a_{n}} = \\frac{2^{n+1}}{(n+1)!}\\cdot\\frac{n!}{2^{n}} = \\frac{2}{n+1}'),
          insight('The terms are shrinking by a factor that itself tends to zero — eventually each term is less than a hundredth of the one before. That is far more decisive than any geometric series, and the ratio test is the statement that noticing this is enough.'),
        ],
      },
      intuition: {
        title: 'One limit, three verdicts',
        blocks: [
          text('**The ratio test.** Compute'),
          math('L = \\lim_{n\\to\\infty}\\left|\\frac{a_{n+1}}{a_{n}}\\right|'),
          table(['L', 'Verdict'], [
            ['L < 1', 'the series converges absolutely'],
            ['L > 1, or L = ∞', 'the series diverges'],
            ['L = 1', 'no information — use a different test'],
          ]),
          text('The reasoning is a comparison with a geometric series after all, but with one you build rather than one you look up: if the ratios settle near L < 1, then far enough out the series is dominated by a geometric series with that ratio, and that converges.'),
          warning('L = 1 is not "nearly convergent" and not "probably convergent". It is the test returning nothing. Every p-series gives L = 1 — for Σ1/n and Σ1/n² alike, the ratio n^p/(n+1)^p tends to 1 — and those two series behave differently. A test that cannot separate the harmonic series from the Basel series has said nothing about either.', 'L = 1 decides nothing'),
          insight('That failure mode is not a defect; it is a fact about what ratios can see. The ratio test measures *exponential* decay, and p-series decay polynomially — too slowly for any ratio limit below 1 to exist. So the rule of thumb is: factorials and n-th powers, use the ratio test; powers of n, do not bother.'),
          text('**The root test.** When the whole term is an n-th power, take the n-th root instead:'),
          math('L = \\lim_{n\\to\\infty}\\sqrt[n]{|a_{n}|}'),
          text('with exactly the same three verdicts. It is the ratio test\'s sibling, and it is easier whenever the term looks like (something)ⁿ:'),
          math('\\sum\\left(\\frac{2n+1}{3n+4}\\right)^{n} \\;\\Longrightarrow\\; L = \\lim_{n\\to\\infty}\\frac{2n+1}{3n+4} = \\frac{2}{3} < 1'),
          tip('The two tests never disagree, and when one gives 1 so does the other — so there is no point trying the second after the first returns no information. Choose between them by the shape of the term: a factorial means ratio, an n-th power means root.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'bar-chart', bars: [
              { label: 'a₁', value: 200 },
              { label: 'a₂', value: 200 },
              { label: 'a₃', value: 133 },
              { label: 'a₄', value: 67 },
              { label: 'a₅', value: 27 },
              { label: 'a₆', value: 9 },
            ], unitLabel: '2ⁿ/n!, ×100' },
            'The terms of Σ2ⁿ/n!. They rise, hold level, and then collapse — because the ratio 2/(n + 1) is greater than 1 while n < 1, equal to 1 at n = 1, and shrinking thereafter. Convergence is decided by that tail behaviour, not by the first few terms, which is why the test takes a limit rather than checking a single ratio.',
          ),
          insight('A series can grow for a hundred terms and still converge. Any test that inspected only the early terms would get this wrong, and the reason all four tests in this unit are stated as limits is precisely that the answer lives in the tail.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Test Σ n²/2ⁿ for convergence.'),
          steps([
            { label: 'Write the ratio of consecutive terms.', latex: '\\frac{a_{n+1}}{a_{n}} = \\frac{(n+1)^{2}}{2^{n+1}}\\cdot\\frac{2^{n}}{n^{2}}' },
            { label: 'The powers of 2 leave one factor of ½.', latex: '= \\frac{1}{2}\\left(\\frac{n+1}{n}\\right)^{2}' },
            { label: 'Take the limit; the bracket tends to 1.', latex: 'L = \\frac{1}{2}' },
            { label: 'L < 1.', latex: '\\text{converges absolutely}' },
          ]),
          text('And a case where the test has nothing to say. Test Σ 1/(n² + 1):'),
          steps([
            { label: 'Form the ratio.', latex: '\\frac{a_{n+1}}{a_{n}} = \\frac{n^{2}+1}{(n+1)^{2}+1}' },
            { label: 'Both top and bottom are quadratics in n.', latex: 'L = 1' },
            { label: 'The verdict.', latex: '\\text{no information — try comparison instead}' },
          ]),
          insight('The second series converges — limit comparison against 1/n² settles it in a line. The ratio test failing to see that is not a mistake in the calculation; it is the test working correctly and returning nothing. Recognising this shape in advance saves the whole calculation: a ratio of polynomials always gives L = 1.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('ratio-test', 4)] },
      independent: { title: 'On your own', blocks: [practice('ratio-test', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('ratio-test', 3, 'Factorials, n-th powers, and ratio limits that return no information at all.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'L = lim |aₙ₊₁/aₙ|. Less than 1 converges absolutely; more than 1 diverges.',
            'L = 1 is no information — not a near miss, and not a lean either way.',
            'Every p-series gives L = 1, so never use the ratio test on one.',
            'Factorials and exponentials are what the ratio test is for.',
            'The root test takes the n-th root, has the same three verdicts, and suits terms that are n-th powers.',
            'The two tests always agree, including on returning nothing.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'alternating-series-01',
    unitId: 'unit-infinite-series',
    conceptId: 'alternating-series',
    title: 'Alternating Series',
    summary: 'Terms that change sign, the sums that exist only because they do, and an error bound you get for nothing.',
    objectives: [
      'Apply the alternating series test, checking both of its conditions',
      'Bound the error in a partial sum by the first term left out',
      'Distinguish absolute from conditional convergence, and say why it matters',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'The series that should not converge',
        blocks: [
          text('The harmonic series diverges. Its terms shrink, and the sum still runs away, because every term pushes in the same direction and the pushes never stop being worth having.'),
          text('Now put a minus sign on every other term:'),
          math('1 - \\tfrac{1}{2} + \\tfrac{1}{3} - \\tfrac{1}{4} + \\tfrac{1}{5} - \\cdots'),
          text('This converges. Its sum is ln 2 ≈ 0.693. Nothing about the *sizes* of the terms changed — only the signs.'),
          insight('The partial sums now overshoot and undershoot alternately, and each correction is smaller than the last, so they close in on a value from both sides like a shrinking interval. Signs, not sizes, are what saves this series, and that observation is both the test and the error bound in this lesson.'),
        ],
      },
      intuition: {
        title: 'Two conditions, and a bound for free',
        blocks: [
          text('**The alternating series test.** For a series Σ(−1)ⁿ⁺¹bₙ with every bₙ > 0, the series converges if both:'),
          list([
            'bₙ is decreasing — each term no larger than the one before, at least eventually; and',
            'bₙ → 0.',
          ], true),
          warning('Both conditions are needed, and dropping the first is the usual slip. A series whose terms tend to zero while jumping up and down in size can diverge even when it alternates. "Alternating with terms going to 0" is not the test; monotone decrease is half of it.', 'Decreasing is not optional'),
          text('**The error bound.** This is the part that makes alternating series unusually useful. If S is the sum and sₙ the n-th partial sum, then'),
          math('|S - s_{n}| \\leq b_{n+1}'),
          text('— the error is no bigger than the first term you did not use.'),
          insight('That is an extraordinarily cheap guarantee. For most convergent series, knowing how close a partial sum is to the answer requires real work; here you read it off the next term. Adding the alternating harmonic series up to n = 999 puts you within 1/1000 of ln 2, and you know that without computing anything.'),
          text('**Absolute and conditional convergence.** Take the absolute value of every term and ask again:'),
          list([
            'If Σ|aₙ| converges, the series converges **absolutely**. This is the strong kind.',
            'If Σaₙ converges but Σ|aₙ| does not, it converges **conditionally**. The convergence depends on the cancellation.',
          ]),
          math('\\sum\\frac{(-1)^{n+1}}{n^{2}} \\text{ converges absolutely}, \\qquad \\sum\\frac{(-1)^{n+1}}{n} \\text{ converges conditionally}'),
          warning('Conditional convergence is fragile in a way that has to be seen to be believed. Riemann proved that the terms of a conditionally convergent series can be *rearranged* to sum to any number you like — or to diverge. The alternating harmonic series adds to ln 2 in the order written, and to 5, or to −17, in some other order. Absolute convergence is exactly the condition that rules this out.', 'Rearrangement can change a conditional sum'),
          tip('The practical order of work: check absolute convergence first, using any positive-term test. If Σ|aₙ| converges you are finished, and with the stronger answer. Only if it does not do you need the alternating series test.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'number-line', from: 0, to: 1.2, step: 0.2, marks: [
              { at: 1, label: 's₁' },
              { at: 0.5, label: 's₂' },
              { at: 0.83, label: 's₃' },
              { at: 0.58, label: 's₄' },
              { at: 0.69, label: 'ln 2' },
            ] },
            'The partial sums of the alternating harmonic series, plotted where they land. Odd ones overshoot, even ones undershoot, and each is closer than the last. The sum is trapped between any two consecutive partial sums — which is the error bound, seen rather than stated.',
          ),
          insight('The trapping is the proof. Consecutive partial sums form a nested sequence of intervals whose widths are the terms bₙ, and those widths go to zero, so exactly one point survives. That point is the sum, and it lies within bₙ₊₁ of sₙ because it is inside that interval.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Show that Σ(−1)ⁿ⁺¹/√n converges, decide whether it does so absolutely, and say how many terms guarantee an error below 0.01.'),
          steps([
            { label: 'Check absolute convergence first.', latex: '\\sum\\frac{1}{\\sqrt{n}} \\text{ is a } p\\text{-series with } p = \\tfrac{1}{2} \\leq 1' },
            { label: 'It diverges, so absolute convergence fails. Try the alternating test.', latex: 'b_{n} = \\frac{1}{\\sqrt{n}}' },
            { label: 'Condition one: decreasing.', latex: '\\frac{1}{\\sqrt{n+1}} < \\frac{1}{\\sqrt{n}} \\quad\\text{for all } n' },
            { label: 'Condition two: tends to zero.', latex: '\\lim_{n\\to\\infty}\\frac{1}{\\sqrt{n}} = 0' },
            { label: 'Both hold, so it converges — conditionally.', latex: '\\text{convergent, not absolutely}' },
            { label: 'Error bound: solve b_{n+1} ≤ 0.01.', latex: '\\frac{1}{\\sqrt{n+1}} \\leq 0.01 \\quad\\Longrightarrow\\quad n + 1 \\geq 10^{4}' },
            { label: 'So 9999 terms suffice.', latex: 'n \\geq 9999' },
          ]),
          insight('Ten thousand terms for two decimal places is dreadful, and it is honest: a conditionally convergent series converges slowly, because it is cancellation rather than smallness doing the work. Series that converge absolutely and fast — the Taylor series two lessons from now — are the ones anyone actually computes with.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('alternating-series', 4)] },
      independent: { title: 'On your own', blocks: [practice('alternating-series', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('alternating-series', 3, 'Both conditions, error bounds, and telling absolute convergence from conditional.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Alternating series test: bₙ decreasing and bₙ → 0. Both conditions, every time.',
            'Error bound: |S − sₙ| ≤ bₙ₊₁ — the first term left out.',
            'Absolute convergence means Σ|aₙ| converges; it is the stronger statement.',
            'Conditional means the series converges only because of the cancellation.',
            'Check absolute convergence first — it is a stronger answer for the same work.',
            'A conditionally convergent series can be rearranged to sum to anything at all.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'power-series-01',
    unitId: 'unit-power-series',
    conceptId: 'power-series',
    title: 'Power Series and the Radius of Convergence',
    summary: 'A polynomial with no last term, and the interval of x it is entitled to say anything about.',
    objectives: [
      'Find the radius of convergence of a power series using the ratio test',
      'Test the two endpoints separately to get the full interval of convergence',
      'Recognise the geometric series as a power series for 1/(1 − x)',
    ],
    estimatedMinutes: 50,
    sections: {
      introduction: {
        title: 'Put an x in it',
        blocks: [
          text('Every series so far has been a list of numbers with one answer: convergent or not. Now let the terms contain a variable.'),
          math('\\sum_{n=0}^{\\infty} c_{n}(x - a)^{n} = c_{0} + c_{1}(x-a) + c_{2}(x-a)^{2} + \\cdots'),
          text('This is a **power series** centred at a. For each particular x it is an ordinary series, so it converges or does not; and the set of x for which it converges is the *domain of a function*.'),
          insight('That is the shift this lesson exists to make. A power series is not a series — it is a function, defined by a series, on whatever set of x the series is willing to converge on. Finding that set is therefore not a technicality before the interesting part. It is the definition of the function.'),
          text('You have already seen one. The geometric series, read as a function of x:'),
          math('\\sum_{n=0}^{\\infty} x^{n} = \\frac{1}{1-x} \\qquad \\text{for } |x| < 1'),
          text('The right-hand side is defined for every x except 1. The left-hand side is defined only on (−1, 1). They are equal exactly where both make sense, and the series says nothing at all about x = 5 — even though 1/(1 − 5) is a perfectly good number.'),
        ],
      },
      intuition: {
        title: 'A radius, then two endpoints',
        blocks: [
          text('The remarkable fact — and it is a theorem, not an observation — is that the set of convergence is always an interval centred at a. There are exactly three possibilities:'),
          table(['Case', 'Converges for', 'Radius R'], [
            ['only at the centre', 'x = a alone', '0'],
            ['on an interval', '|x − a| < R, plus possibly the ends', 'a positive number'],
            ['everywhere', 'every real x', '∞'],
          ]),
          text('**Finding R.** Apply the ratio test to the series with the x in it. The limit will come out as |x − a| times something, and the condition L < 1 is then an inequality for x:'),
          math('L = \\lim_{n\\to\\infty}\\left|\\frac{c_{n+1}(x-a)^{n+1}}{c_{n}(x-a)^{n}}\\right| = |x - a|\\lim_{n\\to\\infty}\\left|\\frac{c_{n+1}}{c_{n}}\\right| < 1'),
          insight('The x factors straight out of the limit, because it does not depend on n. That is why the ratio test — the one test that never needed another series — is the tool for this: it turns a question about convergence into an inequality in x, mechanically.'),
          text('**The endpoints.** The ratio test gives strict inequality, so it says nothing about x = a − R and x = a + R. At those two values L = 1, and you already know that means no information.'),
          warning('The endpoints must be tested separately, one at a time, by substituting the value and using whatever test the resulting number series needs. They can behave differently from each other: a series can converge at one end and diverge at the other, and reporting an open interval without checking is not a rounding of the answer — it is a different answer.', 'The two ends are two more problems'),
          text('So the answer to "where does this converge" has two parts: the radius R, from one ratio-test calculation, and the **interval of convergence**, which is R plus the verdict at each end.'),
          tip('Write the interval with the brackets that say what you found: (a − R, a + R) if both ends diverge, [a − R, a + R] if both converge, and a half-open interval if they disagree. The bracket is carrying information, not punctuation.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'number-line', from: -2, to: 6, step: 1, marks: [
              { at: 2, label: 'centre' },
              { at: -1, label: 'test' },
              { at: 5, label: 'test' },
            ] },
            'The interval of convergence for Σ(x − 2)ⁿ/(n·3ⁿ). The ratio test gives a radius of 3, so convergence is guaranteed strictly between −1 and 5 and refused strictly outside. The two marked ends are the two remaining questions, and they have to be asked one at a time.',
          ),
          insight('At x = 5 the series becomes Σ1/n, the harmonic series, which diverges. At x = −1 it becomes Σ(−1)ⁿ/n, the alternating harmonic series, which converges. Same radius, opposite verdicts, and the interval is [−1, 5) — which no amount of care with the ratio test alone would ever have produced.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the interval of convergence of Σ (x − 2)ⁿ/(n·3ⁿ), summed from n = 1.'),
          steps([
            { label: 'Form the ratio of consecutive terms.', latex: '\\left|\\frac{(x-2)^{n+1}}{(n+1)3^{n+1}}\\cdot\\frac{n\\,3^{n}}{(x-2)^{n}}\\right|' },
            { label: 'The powers cancel down to one factor each.', latex: '= \\frac{|x-2|}{3}\\cdot\\frac{n}{n+1}' },
            { label: 'Take the limit; the n-fraction tends to 1.', latex: 'L = \\frac{|x-2|}{3}' },
            { label: 'Convergence needs L < 1.', latex: '|x - 2| < 3 \\quad\\Longrightarrow\\quad R = 3' },
            { label: 'That is the open interval from −1 to 5. Now the ends.', latex: '-1 < x < 5' },
            { label: 'At x = 5 the numerator is 3ⁿ and cancels.', latex: '\\sum\\frac{3^{n}}{n\\,3^{n}} = \\sum\\frac{1}{n} \\quad\\text{diverges}' },
            { label: 'At x = −1 the numerator is (−3)ⁿ.', latex: '\\sum\\frac{(-1)^{n}}{n} \\quad\\text{converges, by the alternating series test}' },
            { label: 'Assemble.', latex: '\\text{interval of convergence} = [-1,\\ 5)' },
          ]),
          insight('Notice how little the ratio test had to do and how much the endpoints did. That is typical: R is one limit and takes three lines, and the two endpoint checks are two full convergence problems drawn from everything earlier in the unit. This is where the four tests earn their place.'),
          warning('The absolute value in |x − 2| < 3 is not decoration either. Dropping it gives x − 2 < 3, so x < 5, with no lower end at all — an answer that claims the series converges at x = −1000, where the terms grow like 334ⁿ.', 'Keep the absolute value'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('power-series', 4)] },
      independent: { title: 'On your own', blocks: [practice('power-series', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('power-series', 3, 'Radii from the ratio test, endpoints that disagree with each other, and the degenerate radii 0 and ∞.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A power series is a function, and its interval of convergence is the domain.',
            'The set of convergence is always an interval centred at a: radius 0, finite R, or ∞.',
            'Find R with the ratio test — x factors out of the limit.',
            'The ratio test says nothing at the endpoints; test each one separately.',
            'The endpoints can disagree, so the interval can be open, closed or half-open.',
            'Σxⁿ = 1/(1 − x) on (−1, 1), and says nothing anywhere else.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'taylor-series-01',
    unitId: 'unit-power-series',
    conceptId: 'taylor-series',
    title: 'Taylor and Maclaurin Series',
    summary: 'Building a function\'s power series out of its derivatives at one point, and knowing how good the first few terms are.',
    objectives: [
      'Derive the Taylor coefficient formula f⁽ⁿ⁾(a)/n! and use it',
      'Write the Maclaurin series for e^x, sin x, cos x and 1/(1 − x)',
      'Approximate a value with a Taylor polynomial and bound the error',
    ],
    estimatedMinutes: 50,
    sections: {
      introduction: {
        title: 'Which power series is which function?',
        blocks: [
          text('The last lesson took a power series and found the function it defines. This one runs the question backwards: given a function, which power series is it?'),
          text('Suppose f(x) = Σcₙ(x − a)ⁿ on some interval around a. What must the coefficients be? Put x = a and every term with a bracket in it dies:'),
          math('f(a) = c_{0}'),
          text('Differentiate the whole series term by term, then put x = a again:'),
          math('f\'(x) = c_{1} + 2c_{2}(x-a) + 3c_{3}(x-a)^{2} + \\cdots \\quad\\Longrightarrow\\quad f\'(a) = c_{1}'),
          text('Again, and the second derivative of the quadratic term leaves a 2:'),
          math('f\'\'(a) = 2c_{2}, \\qquad f\'\'\'(a) = 6c_{3}, \\qquad f^{(n)}(a) = n!\\,c_{n}'),
          insight('So there is no choice. If a function has a power series at all, its coefficients are forced, and they are the derivatives divided by factorials. That is the entire content of this lesson, and everything else is applying it.'),
        ],
      },
      intuition: {
        title: 'The formula, five examples, and how wrong the truncation is',
        blocks: [
          text('**The Taylor series** of f about a:'),
          math('f(x) = \\sum_{n=0}^{\\infty}\\frac{f^{(n)}(a)}{n!}(x-a)^{n}'),
          text('When a = 0 it has its own name — the **Maclaurin series** — and that is the case that appears most, because the arithmetic is easiest and most functions of interest are being examined near zero.'),
          warning('The factorial is not optional and is the term most often dropped. Without it the series is a list of derivatives, and its sum is not the function: the third Maclaurin coefficient of e^x is 1/6, not 1. A missing n! makes a series that diverges for every x ≠ 0.', 'The n! belongs there'),
          text('**The four to know by heart.** Deriving these each time is possible and slow; every course expects them recalled:'),
          table(['Function', 'Maclaurin series', 'Valid for'], [
            ['e^x', '1 + x + x²/2! + x³/3! + …', 'all x'],
            ['sin x', 'x − x³/3! + x⁵/5! − …', 'all x'],
            ['cos x', '1 − x²/2! + x⁴/4! − …', 'all x'],
            ['1/(1 − x)', '1 + x + x² + x³ + …', '|x| < 1'],
            ['ln(1 + x)', 'x − x²/2 + x³/3 − …', '−1 < x ≤ 1'],
          ]),
          insight('Read the sine and cosine rows together. Sine is odd and has only odd powers; cosine is even and has only even powers. That is not a coincidence to memorise separately — an odd function\'s even derivatives all vanish at 0, so the even coefficients are zero automatically.'),
          text('**Taylor polynomials.** Stop after the n-th term and you have a polynomial, Tₙ(x), which is what a calculator actually evaluates. The error is the rest of the series, and Taylor\'s theorem bounds it:'),
          math('|f(x) - T_{n}(x)| \\leq \\frac{M}{(n+1)!}|x-a|^{n+1}'),
          text('where M is any bound on |f⁽ⁿ⁺¹⁾| between a and x.'),
          insight('The (n + 1)! in the denominator is why this works so well near the centre. Doubling the number of terms does not halve the error; it divides it by a factorial. Three terms of the series for e^x give e^{0.1} correct to five decimal places.'),
          warning('Near the centre. The same bound has |x − a|^{n+1} on top, and far from a that factor wins for a while — which is why using a Maclaurin series to compute sin(100) is a bad idea even though the series converges for every x. Centre the expansion near the value you want.', 'Accuracy is local'),
          text('**Building new series from old.** Substituting, multiplying by a power, differentiating and integrating are all legal inside the radius, and they are far faster than computing derivatives:'),
          math('e^{-x^{2}} = 1 - x^{2} + \\frac{x^{4}}{2!} - \\frac{x^{6}}{3!} + \\cdots'),
          tip('That one is worth its own note: e^{−x²} has no elementary antiderivative at all, and yet its series integrates term by term without difficulty. Power series make previously impossible integrals routine, and that is the practical reason this unit exists.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'sine',
              lines: [{ slope: 1, intercept: 0, label: 'T₁(x) = x', dashed: true }],
              min: -4,
              max: 4,
              label: 'y = sin x',
            },
            'The first Taylor polynomial of sin x is the line y = x, and near the origin the two are indistinguishable — which is exactly the small-angle approximation from physics, now with an error bound attached. Further out the line leaves the curve entirely, and adding the −x³/6 term is what pulls it back.',
          ),
          insight('This is the picture to hold for what a Taylor polynomial does: it matches the function\'s value, then its slope, then its curvature, then the rate of change of its curvature, at one single point — and hopes that agreeing about everything at one point is enough to agree nearby. Astonishingly, for most functions, it is.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the Maclaurin series for e^x, then use three terms to estimate e^{0.1} and bound the error.'),
          steps([
            { label: 'Every derivative of e^x is e^x.', latex: 'f^{(n)}(x) = e^{x} \\quad\\Longrightarrow\\quad f^{(n)}(0) = 1' },
            { label: 'So every coefficient is 1/n!.', latex: 'e^{x} = \\sum_{n=0}^{\\infty}\\frac{x^{n}}{n!} = 1 + x + \\frac{x^{2}}{2} + \\frac{x^{3}}{6} + \\cdots' },
            { label: 'Take three terms and put x = 0.1.', latex: 'T_{2}(0.1) = 1 + 0.1 + 0.005 = 1.105' },
            { label: 'Bound the third derivative on [0, 0.1].', latex: '|f\'\'\'| = e^{x} \\leq e^{0.1} < 1.2 = M' },
            { label: 'Apply the error bound with n = 2.', latex: '|e^{0.1} - T_{2}(0.1)| \\leq \\frac{1.2}{3!}(0.1)^{3} = 0.0002' },
            { label: 'Compare with the true value.', latex: 'e^{0.1} = 1.10517\\ldots \\quad\\text{error} \\approx 0.00017' },
          ]),
          insight('The bound promised 0.0002 and the actual error was 0.00017 — tight, because the bound is not a crude estimate but the next term of the series with the derivative maximised. Taylor\'s theorem is one of the few error bounds in mathematics that is worth quoting rather than ignoring.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('taylor-series', 4)] },
      independent: { title: 'On your own', blocks: [practice('taylor-series', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('taylor-series', 3, 'Coefficients from derivatives, series built by substitution, and error bounds on a truncation.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'cₙ = f⁽ⁿ⁾(a)/n! — the coefficients are forced, not chosen.',
            'Maclaurin is Taylor at a = 0.',
            'Know e^x, sin x, cos x, 1/(1 − x) and ln(1 + x) by heart.',
            'The n! is part of the coefficient. Dropping it makes a divergent series.',
            'Error after n terms is at most M|x − a|^{n+1}/(n + 1)!.',
            'Substituting into a known series beats differentiating n times, every time.',
          ]),
        ],
      },
    },
  }),
];
